/** /presets — grid preset management (console_presets_manage only).
 *
 * Lists ALL presets across owners (backend `all=true` mode), with rename/edit
 * (raw settings JSON), delete, and per-preset change history + restore.
 */
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import { Box, Chip, CircularProgress, IconButton, Stack, Tooltip } from '@mui/material';
import { getGridStringOperators, type GridColDef } from '@mui/x-data-grid-pro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import * as presetsApi from '@/api/presets';
import { presetErrorMessage, type GridPresetListItem } from '@/api/presets';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { formatRelativeTime } from '@/utils/format';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { PresetEditDialog } from './PresetEditDialog';
import { PresetHistoryDialog } from './PresetHistoryDialog';

const containsOnly = getGridStringOperators().filter((op) => op.value === 'contains');

/** The name column header filter maps to the backend `search` (icontains). */
const FILTERS: FilterDef[] = [
  { param: 'q', apiParam: 'search', column: { field: 'name', operator: 'contains' } },
  { param: 'grid_filter', apiParam: 'grid' },
];

/** Every grid has presets — including this one (codename 'grid_presets'). */
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

/** Column field order (mirrors the columns memo — keep in sync). */
const FIELD_ORDER = ['id', 'grid', 'name', 'visibility', 'owner', 'updated_ts', 'created_ts', '__actions__'];

export function PresetsManagePage() {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<number | null>(null);
  const [historyFor, setHistoryFor] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<GridPresetListItem | null>(null);

  const presets = useGridPresets({
    grid: 'grid_presets',
    defaultOrder: FIELD_ORDER,
    defaultHidden: ['created_ts'],
    defaultOrdering: '-updated_ts',
    filters: PRESET_FILTERS,
  });
  const gf = useGridFilters(FILTERS, presets.filtersLayer);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => presetsApi.remove(id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: ['gridPresetsAll'] });
      void qc.invalidateQueries({ queryKey: ['gridPresets'] });
      qc.removeQueries({ queryKey: ['gridPreset', id] });
      snackbar.show('Preset deleted. Its history is retained.', 'success');
      setDeleting(null);
    },
    onError: (e: unknown) => {
      snackbar.show(presetErrorMessage(e), 'error');
      setDeleting(null);
    },
  });

  const columns = useMemo<GridColDef<GridPresetListItem>[]>(
    () => [
      { field: 'id', headerName: 'ID', width: 80, filterable: false },
      {
        field: 'grid',
        headerName: 'Grid',
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (p) => <Chip size="small" variant="outlined" label={p.row.grid} />,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.4,
        filterOperators: containsOnly,
      },
      {
        field: 'visibility',
        headerName: 'Visibility',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Chip
            size="small"
            {...(p.row.visibility === 'shared'
              ? { color: 'primary' as const }
              : { variant: 'outlined' as const })}
            label={p.row.visibility}
          />
        ),
      },
      {
        field: 'owner',
        headerName: 'Owner',
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (p) => p.row.owner.email,
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 150,
        filterable: false,
        renderCell: (p) => formatRelativeTime(p.row.updated_ts),
      },
      {
        field: 'created_ts',
        headerName: 'Created',
        width: 150,
        // Not in the backend _ORDERING_FIELDS — display-only.
        sortable: false,
        filterable: false,
        renderCell: (p) => formatRelativeTime(p.row.created_ts),
      },
      {
        field: '__actions__',
        headerName: '',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit (name, shared, settings JSON)">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(p.row.id);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Change history / restore">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setHistoryFor(p.row.id);
                }}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleting(p.row);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  );

  const queryFn = (params: ResourceDataGridParams) =>
    presetsApi.list({
      all: true,
      limit: params.pageSize,
      offset: (params.page - 1) * params.pageSize,
      ordering: params.ordering ?? presets.ordering,
      ...(params.extra as { search?: string; grid?: string }),
    });

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="ui-grid-presets"
        title="Grid Presets"
        subtitle="Every saved grid view across all users — rename, edit the raw settings, inspect history, restore."
      />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<GridPresetListItem>
          queryKey={['gridPresetsAll']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={gf.extra}
          filterModel={gf.filterModel}
          onFilterModelChange={gf.onFilterModelChange}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          onRowClick={(id) => setEditing(Number(id))}
          headerToolbar={{
            onResetAllFilters: gf.resetAll,
            resetDisabled: !gf.anyActive,
            exportFileName: 'grid-presets',
            presets: presets.toolbar,
          }}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      )}

      {editing !== null && <PresetEditDialog presetId={editing} onClose={() => setEditing(null)} />}
      {historyFor !== null && (
        <PresetHistoryDialog presetId={historyFor} onClose={() => setHistoryFor(null)} />
      )}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete preset?"
        message={`Delete '${deleting?.name ?? ''}' (owned by ${deleting?.owner.email ?? ''})? The change history is retained.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (deleting) deleteMutation.mutate(deleting.id);
        }}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
